import { useEffect } from 'react';
import { useLegalSettings, orPlaceholder } from '../hooks/useLegalSettings';
import { useLang } from '../contexts/LanguageContext';

export default function Legal() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { settings, loading } = useLegalSettings();
  const { t } = useLang();
  const ph = t('privacy.placeholder'); // helper formatter pour les [À compléter]

  const s = settings || {};
  const updateLabel = s.updatedAtLabel || '—';
  const email = s.email || 'contact@pernetyparis.com';

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />
        <div className="relative z-10 text-center px-6">
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="w-12 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-10 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-12 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(36px, 5vw, 60px)'
          }}>
            {t('legal.heroTitle')}
          </h1>
        </div>
      </section>

      {/* ── CONTENU ── */}
      <section className="bg-white py-20 md:py-24 px-6">
        <div className="max-w-3xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>

          <p className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-12 text-center">
            {t('legal.lastUpdate')} : {updateLabel}
          </p>

          {loading ? (
            <p className="text-center text-gray-400">{t('common.loading')}</p>
          ) : (
          <>

          <Block title={t('legal.s1Title')}>
            <p>
              {t('legal.s1Intro')} <strong>pernetyparis.com</strong> {t('legal.s1Edited')}
            </p>
            <ul className="space-y-1 my-4">
              <li><strong>{t('legal.brand')} :</strong> Pernety</li>
              <li><strong>{t('legal.responsible')} :</strong> {orPlaceholder(s.legalName, 'Prénom NOM', ph)}</li>
              <li><strong>{t('legal.legalStatus')} :</strong> {orPlaceholder(s.legalStatus, 'ex. Entrepreneur individuel (EI)', ph)}</li>
              <li><strong>{t('legal.address')} :</strong> {orPlaceholder(s.address, 'adresse complète', ph)}</li>
              <li><strong>{t('legal.siret')} :</strong> {orPlaceholder(s.siret, '14 chiffres', ph)}</li>
              <li><strong>{t('legal.apeCode')} :</strong> {orPlaceholder(s.apeCode, 'ex. 32.12Z', ph)}</li>
              <li><strong>{t('legal.vat')} :</strong> {orPlaceholder(s.vatNumber, 'numéro ou « Non assujettie — art. 293 B du CGI »', ph)}</li>
              {s.capital && s.capital.trim() && (
                <li><strong>{t('legal.capital')} :</strong> {s.capital}</li>
              )}
              {s.rcs && s.rcs.trim() && (
                <li><strong>{t('legal.rcs')} :</strong> {s.rcs}</li>
              )}
              <li><strong>{t('legal.phoneLabel')} :</strong> {orPlaceholder(s.phone, 'numéro de téléphone', ph)}</li>
              <li><strong>{t('legal.emailLabel')} :</strong> {email}</li>
            </ul>
          </Block>

          <Block title={t('legal.s2Title')}>
            <p>
              {t('legal.s2P')(orPlaceholder(s.legalName, 'Prénom NOM', ph), orPlaceholder(s.publicationManagerRole, 'ex. fondatrice', ph))}
            </p>
          </Block>

          <Block title={t('legal.s3Title')}>
            <p>{t('legal.s3Intro')}</p>
            <ul className="space-y-1 my-4">
              <li><strong>{t('legal.hostCompany')} :</strong> OVH SAS</li>
              <li><strong>{t('legal.hostAddress')} :</strong> 2 rue Kellermann, 59100 Roubaix, France</li>
              <li><strong>{t('legal.hostPhone')} :</strong> +33 (0)9 72 10 10 07</li>
              <li><strong>{t('legal.hostWebsite')} :</strong> <a href="https://www.ovhcloud.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">www.ovhcloud.com</a></li>
            </ul>
          </Block>

          <Block title={t('legal.s4Title')}>
            <p>{t('legal.s4P1')}</p>
            <p>{t('legal.s4P2')}</p>
            <p>{t('legal.s4P3')(orPlaceholder(s.trademarkOwner, 'titulaire des droits + n° INPI si applicable', ph))}</p>
          </Block>

          <Block title={t('legal.s5Title')}>
            <ul className="space-y-1 my-4">
              <li><strong>{t('legal.creditsDev')} :</strong> {s.creditsDeveloper || 'Webcraft'}</li>
              <li><strong>{t('legal.creditsPhoto')} :</strong> {orPlaceholder(s.creditsPhotographer, 'nom du/des photographe(s)', ph)}</li>
              {s.creditsDesigner && s.creditsDesigner.trim() && (
                <li><strong>{t('legal.creditsDesign')} :</strong> {s.creditsDesigner}</li>
              )}
            </ul>
          </Block>

          <Block title={t('legal.s6Title')}>
            <p>{t('legal.s6P1')}</p>
            <p>{t('legal.s6P2')}</p>
            <p>{t('legal.s6P3')}</p>
          </Block>

          <Block title={t('legal.s7Title')}>
            <p>{t('legal.s7P1')}</p>
            <p>{t('legal.s7P2')}</p>
          </Block>

          <Block title={t('legal.s8Title')}>
            <p>
              {t('legal.s8P')} <a href="/politique-confidentialite" className="underline hover:text-black">{t('legal.s8Link')}</a>{t('legal.s8End')}
            </p>
          </Block>

          <Block title={t('legal.s9Title')}>
            <p>{t('legal.s9P')}</p>
          </Block>

          <Block title={t('legal.s10Title')}>
            <p>
              {t('legal.s10P')(email)}
            </p>
          </Block>

          </>
          )}

        </div>
      </section>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="mb-12">
      <h2 className="font-light mb-5 text-gray-900" style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 'clamp(20px, 1.8vw, 26px)'
      }}>
        {title}
      </h2>
      <div className="space-y-4 text-[14.5px] leading-[1.85]" style={{ color: '#4a4a4a' }}>
        {children}
      </div>
    </div>
  );
}
